class GoogleSheetsService {
  private static instance: GoogleSheetsService;
  private accessToken: string | null = null;
  private isInitialized = false;

  private readonly CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  private readonly SCOPES = 'https://www.googleapis.com/auth/spreadsheets.readonly';

  static getInstance(): GoogleSheetsService {
    if (!GoogleSheetsService.instance) {
      GoogleSheetsService.instance = new GoogleSheetsService();
    }
    return GoogleSheetsService.instance;
  }

  async initialize(): Promise<boolean> {
    try {
      if (this.isInitialized) return true;

      if (!this.CLIENT_ID) {
        throw new Error('Missing Google Client ID. Please check your environment variables.');
      }

      await this.loadGoogleIdentityServices();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Google Sheets service initialization failed:', error);
      return false;
    }
  }

  private loadGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Reuse if already loaded (e.g. by GmailService)
      if ((window as any).google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }

  async authenticate(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      return new Promise((resolve, reject) => {
        const google = (window as any).google;
        if (!google?.accounts) {
          reject(new Error('Google Identity Services not loaded'));
          return;
        }

        const tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: this.CLIENT_ID,
          scope: this.SCOPES,
          callback: (response: any) => {
            if (response.error) {
              console.error('OAuth error:', response.error);
              reject(new Error(response.error));
              return;
            }
            this.accessToken = response.access_token;
            resolve(true);
          },
        });

        tokenClient.requestAccessToken();
      });
    } catch (error) {
      console.error('Google Sheets authentication failed:', error);
      return false;
    }
  }

  async signOut(): Promise<void> {
    if (this.accessToken) {
      const google = (window as any).google;
      if (google?.accounts?.oauth2) {
        google.accounts.oauth2.revoke(this.accessToken);
      }
    }
    this.accessToken = null;
  }

  isSignedIn(): boolean {
    return this.accessToken !== null;
  }

  parseSpreadsheetId(url: string): string | null {
    // Matches /spreadsheets/d/SPREADSHEET_ID
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  }

  async fetchRows(
    spreadsheetId: string,
    sheetName: string = 'Form Responses 1',
    lastN: number = 20
  ): Promise<string[][]> {
    if (!this.accessToken) {
      throw new Error('Not authenticated with Google Sheets');
    }

    const range = encodeURIComponent(sheetName);
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Sheets API error: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    const result = await response.json();
    const allRows: string[][] = result.values || [];

    if (allRows.length <= 1) {
      // No data rows (only header or empty)
      return [];
    }

    // Skip header row (index 0), return last N data rows
    const dataRows = allRows.slice(1);
    return dataRows.slice(Math.max(0, dataRows.length - lastN));
  }
}

export default GoogleSheetsService;
